import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  Grid,
  IconButton,
  Stack,
  styled,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm
} from 'react-hook-form';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import DetailsCard from '../../../../components/DetailsCard/DetailsCard';
import FormatPhone from '../../../../components/FormatNumber/FormatPhone';
import FormatPhoneInput from '../../../../components/FormatNumber/FormatPhoneInput';
import PageTitle from '../../../../components/PageTitle/PageTitle';
import { dashBorder } from '../../../../constants';
import { Manager } from '../../../../models/manager';
import { managerService } from '../../../../services/managerService';
import { userService } from '../../../../services/userService';
import ManagerDetailsForm from './ManagerDetailsForm';
import DefaultModal from '../../../../components/Modals/DefaultModal';
import IconSuccess from '../../../../assets/icon-success.svg';
import TypographyDash from '../../../../components/TypographyDash/TypographyDash';

const BoldText = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.grey['900'],
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  margin: 0
}));

const BoldModalText = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.grey['900']
}));

const managerDataCols = ['First Name', 'Last Name', 'Email', 'Phone'];

const schema = yup.object({
  firstName: yup.string().required(),
  lastName: yup.string().required(),
  email: yup.string().required(),
  phoneNumber: yup.string().required(),
  notes: yup.string().nullable()
});

const ManagerDetails = (): JSX.Element => {
  const { id } = useParams();
  const [manager, setManager] = useState<Manager | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState('');

  const methods = useForm<Manager>({
    resolver: yupResolver(schema)
  });

  const { handleSubmit, reset, control } = methods;

  useEffect(() => {
    getManager();
  }, [id]);

  const closeEditingForm = () => {
    setIsEditing(false);
    reset(manager);
  };

  const closeSuccessModal = () => {
    getManager();
    setModalOpen(false);
    setMessage('');
    setIsEditing(false);
  };

  const handleSuccessModal = () => {
    if (manager?.id) {
      userService.sendCredentials([manager.id]).then(() => {
        setMessage('Credentials have been sent!');
      });
    }
    setIsEditing(false);
  };

  const onSubmit: SubmitHandler<Manager> = (data) => {
    updateManager(data);
  };

  const updateManager = (manager: Manager) => {
    managerService.updateManager(manager).then(() => {
      setManager(manager);
      setModalOpen(true);
    });
  };

  const getManager = () => {
    managerService.getManagerById(Number(id)).then((response) => {
      setManager(response.data);
      reset(response.data);
    });
  };

  return (
    <>
      <PageTitle>Edit User</PageTitle>
      <DetailsCard
        title={`${manager?.firstName ?? '-'} ${manager?.lastName ?? '-'}`}
        subtitle="Company manager"
      >
        <Grid container spacing={5}>
          <Grid item md={6} xs={12}>
            <Box
              sx={{
                ...dashBorder
              }}
            >
              {!isEditing && (
                <>
                  <Box
                    sx={{
                      mb: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Typography variant="h6" color="grey.900">
                      Personal Information
                    </Typography>
                    <IconButton onClick={() => setIsEditing(true)}>
                      <EditIcon sx={{ cursor: 'pointer' }} />
                    </IconButton>
                  </Box>
                  <Box display="flex" gap={4}>
                    <Stack spacing={2} flexShrink={0}>
                      {managerDataCols.map((col) => (
                        <Typography key={col} color="black" fontWeight="bold">
                          {col}
                        </Typography>
                      ))}
                    </Stack>
                    <Stack
                      spacing={2}
                      display="flex"
                      justifyContent="center"
                      flexShrink={0}
                    >
                      <TypographyDash value={manager?.firstName} />
                      <TypographyDash value={manager?.lastName} />
                      <TypographyDash value={manager?.email} />
                      <Typography>
                        {manager?.phoneNumber ? (
                          <FormatPhone value={manager?.phoneNumber} />
                        ) : (
                          '--'
                        )}
                      </Typography>
                    </Stack>
                  </Box>
                </>
              )}
              {isEditing && (
                <>
                  <Box
                    sx={{
                      mb: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Typography variant="h6" color="grey.900">
                      Personal Information
                    </Typography>
                    <IconButton onClick={closeEditingForm}>
                      <CloseIcon sx={{ cursor: 'pointer' }} />
                    </IconButton>
                  </Box>
                  <Box display="flex" gap={3}>
                    <Stack spacing={1} sx={{ flexShrink: 0 }}>
                      {managerDataCols.map((col) => (
                        <BoldText key={col}>{col}</BoldText>
                      ))}
                    </Stack>
                    <FormProvider {...methods}>
                      <form>
                        <ManagerDetailsForm />
                      </form>
                    </FormProvider>
                  </Box>
                </>
              )}
            </Box>
          </Grid>
          <Grid item md={6} xs={12}>
            <Box
              sx={{
                ...dashBorder,
                height: '100%'
              }}
            >
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  justifyContent: 'start',
                  alignItems: 'center'
                }}
              >
                <Typography variant="h6" color="grey.900">
                  Company Admin Notes
                </Typography>
              </Box>
              <Box display="flex" gap={3}>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="notes"
                      name="notes"
                      placeholder="Manager Notes..."
                      multiline
                      rows={3}
                      inputProps={{
                        maxLength: 200
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
            my: 3
          }}
        >
          <Button
            onClick={handleSubmit((data) => onSubmit(data))}
            sx={{ width: '150px', mr: 2 }}
          >
            Save
          </Button>
        </Box>
      </DetailsCard>
      <DefaultModal open={modalOpen} handleClose={closeSuccessModal}>
        <Box sx={{ px: 6 }}>
          <Box textAlign="center" mb={3}>
            <img src={IconSuccess} alt="success" />
          </Box>
          <Typography variant="h3" textAlign="center" color="grey.900">
            Success!
          </Typography>
          <Typography variant="h5" textAlign="center" color="grey.900">
            Manager has been updated
          </Typography>
          {message && (
            <Alert sx={{ my: 4 }} severity="success">
              {message}
            </Alert>
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 3
            }}
            gap={3}
          >
            <Stack spacing={1}>
              <BoldModalText>First Name</BoldModalText>
              <BoldModalText>Last Name</BoldModalText>
              <BoldModalText>Email Address</BoldModalText>
              <BoldModalText>Phone Number</BoldModalText>
            </Stack>
            <Stack spacing={1}>
              <TypographyDash value={manager?.firstName} />
              <TypographyDash value={manager?.lastName} />
              <TypographyDash value={manager?.email} />
              <TextField
                placeholder="Phone Number"
                size="small"
                value={manager?.phoneNumber ?? '--'}
                sx={{
                  '& input': { padding: 0 },
                  '& fieldset': { border: 'none' }
                }}
                InputProps={{
                  // eslint-disable-next-line
                  inputComponent: FormatPhoneInput as any,
                  readOnly: true
                }}
              />
            </Stack>
          </Box>
        </Box>
        <DialogActions sx={{ justifyContent: 'center', p: 3 }}>
          <Button onClick={closeSuccessModal} sx={{ width: '200px' }}>
            Close
          </Button>
          <Button onClick={handleSuccessModal}>Resend Credentials</Button>
        </DialogActions>
      </DefaultModal>
    </>
  );
};

export default ManagerDetails;
